import React from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import Skeleton from 'react-loading-skeleton'
import { useTheme } from 'styled-components'
import { albumCovertArtPlaceholder } from 'images'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { MaterialIcon } from '@dsny/dsny-component-library'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { AIFilled, PlayArrowFilled } from '@dsny/dsny-component-library/icons'
import {
  LegacyCoverArt,
  CanadianContentPill,
  LegacyCoverArtContainer,
  ReleaseInformationLevelCoverArtCol,
  CanadianPillWrapper,
  OptionsWrapper,
  PlayIconWrapper,
  ReleaseInfoContainer
} from '../../ReleaseInformation.styles'
import {
  ReleaseTitle,
  ReleaseArtist,
  Label,
  Formats,
  ContainsAIPill,
  ReleaseDetailsContentCol
} from './ReleaseDetailsLegacy.styles'

import {
  formatDateWithTimeZoneFromSeconds,
  DATE_FORMAT
} from 'utils/dateHelpers'
import CoverArtPreview from '../CoverArtPreview/CoverArtPreview'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

const AI_POLICY_URL = 'https://support.plaympe.com/ai-policy' // TODO: Update with actual AI policy URL

const ReleaseDetailsLegacy = ({ release, setIsModalOpen, loading }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()

  const handlePlayRelease = (release) => {
    dispatch(
      PlayQueueThunks.playRelease(release.releaseid, {
        source: PLAY_SOURCES.RELEASE_INFORMATION,
        trigger: PLAY_TRIGGERS.COVER_ART
      })
    )
  }

  return (
    <ReleaseInfoContainer $responsiveBreakpointsV2={responsiveBreakpointsV2}>
      <ReleaseInformationLevelCoverArtCol
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
      >
        <LegacyCoverArtContainer
          $responsiveBreakpointsV2={responsiveBreakpointsV2}
        >
          <LegacyCoverArt
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
            src={release?.coverart || albumCovertArtPlaceholder}
            alt="Cover art"
          />
          <CoverArtPreview onExpand={() => setIsModalOpen(true)} />
        </LegacyCoverArtContainer>
        {isMobile && (
          <OptionsWrapper>
            <CanadianPillWrapper>
              {release?.markettags?.includes('cancon') && (
                <CanadianContentPill
                  isMobile={isMobile}
                  variant="tonal"
                  mode="dark"
                  label={t('release-info.canadian')}
                  icon={<MaterialIcon name="CanadaFlag" />}
                  style={{ padding: '4px 8px' }}
                />
              )}
              {release?.is_ai_generated && (
                <ContainsAIPill
                  variant="tonal"
                  mode="dark"
                  label=""
                  onClick={() =>
                    window.open(AI_POLICY_URL, '_blank', 'noopener,noreferrer')
                  }
                  style={{ padding: '4px 8px' }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <AIFilled width={16} height={16} color="currentColor" />
                    <span>{t('release-info.contains-ai')}</span>
                    <MaterialIcon name="OpenInNew" fontSize={14} />
                  </div>
                </ContainsAIPill>
              )}
            </CanadianPillWrapper>
            <PlayIconWrapper>
              <PlayArrowFilled
                width={24}
                color={theme.colors.neutralW20}
                onClick={() => handlePlayRelease(release)}
              />
            </PlayIconWrapper>
          </OptionsWrapper>
        )}
      </ReleaseInformationLevelCoverArtCol>
      <ReleaseDetailsContentCol
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isMobile && release?.markettags?.includes('cancon') && (
            <CanadianContentPill
              isMobile={isMobile}
              variant="tonal"
              mode="dark"
              label={t('release-info.canadian-content')}
              icon={<MaterialIcon name="CanadaFlag" />}
              style={{ padding: '4px 8px' }}
            />
          )}
          {!isMobile && release?.is_ai_generated && (
            <ContainsAIPill
              variant="tonal"
              mode="dark"
              label=""
              onClick={() =>
                window.open(AI_POLICY_URL, '_blank', 'noopener,noreferrer')
              }
              style={{ padding: '4px 8px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <AIFilled width={16} height={16} color="currentColor" />
                <span>{t('release-info.contains-ai')}</span>
                <MaterialIcon name="OpenInNew" fontSize={14} />
              </div>
            </ContainsAIPill>
          )}
        </div>
        <ReleaseTitle data-cy="release-detail-title">
          {release?.title || <Skeleton width={100} />}
        </ReleaseTitle>
        <ReleaseArtist data-cy="release-detail-artist">
          {release?.artist || <Skeleton width={150} />}
        </ReleaseArtist>
        <Label data-cy="release-detail-meta">
          {release?.available > 0 &&
            `${t('release-info.available-date')}: ${format(
              new Date(release?.available),
              DATE_FORMAT
            )}`}

          {loading && <Skeleton width={150} />}
        </Label>
        <Label>
          {release?.impactdate > 0 &&
            `${t(
              'release-info.impact-date'
            )}: ${formatDateWithTimeZoneFromSeconds(release?.impactdate)}`}

          {loading && <Skeleton width={150} />}
        </Label>
        <Formats>
          {release?.genres?.map((genre, index) => (
            <Label key={index} style={{ display: 'inline-flex' }}>
              {genre} {index < release?.genres.length - 1 && ' / '}
            </Label>
          )) || <Skeleton width={200} count={3} />}
        </Formats>
      </ReleaseDetailsContentCol>
    </ReleaseInfoContainer>
  )
}

export default ReleaseDetailsLegacy
